import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, Wrench, UserRound } from "lucide-react";
import { api, fmtErr } from "../../lib/api";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import SupplierTaskDetail from "./maintenance/SupplierTaskDetail";
import { ShowInvoiceButton } from "./maintenance/components";
import { StatusBadge } from "./Dashboard";

function Section({ icon: Icon, title, count, children }) {
  return (
    <section className="mb-6">
      <h2 className="font-display text-lg mb-2 flex items-center gap-2">
        <Icon className="h-4 w-4 text-zinc-400" />{title}
        {count > 0 && <Badge variant="secondary" className="text-[10px]">{count}</Badge>}
      </h2>
      {children}
    </section>
  );
}

function JobRow({ j, onView }) {
  return (
    <div
      className="rounded-lg border border-zinc-200 p-3 cursor-pointer transition-colors hover:border-zinc-400 hover:bg-zinc-50"
      data-testid={`job-${j.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onView(j)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onView(j); } }}
    >
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          {j.yacht_name && <div className="text-[11px] text-zinc-500 truncate">{j.yacht_name}</div>}
          <div className="font-medium text-sm">{j.title}</div>
          {j.description && <div className="text-xs text-zinc-500 line-clamp-2">{j.description}</div>}
        </div>
        <div className="shrink-0 text-right">
          <StatusBadge value={j.status} />
          {j.cancel_request && j.status !== "cancelled" && (
            <div className="mt-1 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" data-testid={`job-cancel-pending-${j.id}`}>
              Cancellation pending
            </div>
          )}
          <div className="text-[10px] text-zinc-400 mt-0.5" data-testid={`job-status-date-${j.id}`}>
            {(j.status_changed_at || j.updated_at || j.created_at || "").slice(0, 10)}
          </div>
          {["confirmed", "full_payment_confirmed"].includes(j.status) && (
            <div className="mt-1.5">
              <ShowInvoiceButton invoiceId={j.invoice_id} supplier testid={`sup-show-invoice-btn-${j.id}`} />
            </div>
          )}
        </div>
      </div>
      <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-[11px] text-zinc-500">
        {j.system_category && <span className="font-medium text-zinc-700">{j.system_category}</span>}
        {j.priority && <span className="capitalize">{j.system_category ? "· " : ""}{j.priority}</span>}
        {(j.due_date || "").slice(0, 10) && <span>· due {(j.due_date || "").slice(0, 10)}</span>}
        {(j.handled_by_name || j.handler_name) && (
          <Badge variant="outline" className="text-[10px] px-1.5 py-0 h-4 border-sky-200 bg-sky-50 text-sky-700 font-normal" data-testid={`job-handledby-${j.id}`}>
            <UserRound className="h-2.5 w-2.5 mr-1" />{j.handled_by_name || j.handler_name}
          </Badge>
        )}
      </div>
    </div>
  );
}

export default function SupplierTasks() {
  const [jobs, setJobs] = useState(null);
  const [openJob, setOpenJob] = useState(null);
  const [handlerFilter, setHandlerFilter] = useState("all");

  const loadJobs = useCallback(async () => {
    try { const { data } = await api.get("/supplier/assigned-jobs"); setJobs(data); }
    catch (e) { toast.error(fmtErr(e)); }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  // ?open={maintId} deep-link (from an invoice's "Show task" button) — pop
  // the task detail for that job, then clean the URL.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const openId = searchParams.get("open");
    if (!openId || jobs === null) return;
    const job = [...(jobs.maintenance || []), ...(jobs.tasks || [])].find((j) => j.id === openId);
    if (job) setOpenJob(job);
    const next = new URLSearchParams(searchParams);
    next.delete("open");
    setSearchParams(next, { replace: true });
  }, [jobs, searchParams, setSearchParams]);

  const allJobs = [...(jobs?.maintenance || []), ...(jobs?.tasks || [])];
  const handlerOf = (j) => j.handled_by_name || j.handler_name || "";
  const handlers = [...new Set(allJobs.map(handlerOf).filter(Boolean))].sort();
  const visibleJobs = handlerFilter === "all" ? allJobs : allJobs.filter((j) => handlerOf(j) === handlerFilter);

  return (
    <div className="max-w-3xl mx-auto px-4 py-6" data-testid="supplier-tasks-page">
      <div className="mb-4 flex items-center justify-between gap-3">
        <h1 className="font-display text-2xl">Tasks</h1>
        {handlers.length > 0 && (
          <Select value={handlerFilter} onValueChange={setHandlerFilter}>
            <SelectTrigger className="w-48 h-8 text-xs" data-testid="handler-filter">
              <SelectValue placeholder="All teammates" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="handler-filter-all">All teammates</SelectItem>
              {handlers.map((h) => (
                <SelectItem key={h} value={h} data-testid={`handler-filter-${h}`}>{h}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {jobs === null ? (
        <div className="py-10 text-center text-zinc-400"><Loader2 className="h-5 w-5 animate-spin inline" /></div>
      ) : (
        <div data-testid="supplier-tasks-jobs">
          <Section icon={Wrench} title="Assigned to me" count={visibleJobs.length}>
            {visibleJobs.length === 0 ? <p className="text-sm text-zinc-500">{handlerFilter === "all" ? "No jobs assigned to you yet." : "No jobs for this teammate."}</p> :
              <div className="space-y-2">{visibleJobs.map((j) => <JobRow key={j.id} j={j} onView={setOpenJob} />)}</div>}
          </Section>
        </div>
      )}

      {openJob && (
        <SupplierTaskDetail
          job={openJob}
          onClose={() => setOpenJob(null)}
          onRefresh={loadJobs}
        />
      )}
    </div>
  );
}
